<script lang="ts">
	import {
		Button,
		Col,
		Container,
		Modal,
		Row,
		Text,
		Alert,
		Stack,
		BulletedList,
	} from '@Shelly/shelly-design-system/index';

	import { format } from 'svelte-i18n';
	import { createEventDispatcher } from 'svelte';
	import type { Device } from 'src/types/Device';
	import { clonableSettings } from 'src/helpers/settings/settings_config_helpers';
	import { formatTemplateValue } from 'src/helpers/settings/template_helpers';
	import { devices_settings } from 'src/stores/device-runtime/status';

	export let showModal = false;
	export let device: Device;
	export let settingsToApply: any;
	export let selectedSettingsKeys: string[] = [];

	const dispatch = createEventDispatcher();

	$: currentSettings = devices_settings.getByRealId(device.id)?.[`switch:${device.channel}`] || {};

	$: settingsComparison = (() => {
		const comparison = [];
		const settings = clonableSettings();

		const filteredSettings = settings.filter((setting) =>
			selectedSettingsKeys.includes(setting.key),
		);

		filteredSettings.forEach((setting) => {
			const currentValue = currentSettings[setting.key];
			const newValue = settingsToApply?.[setting.key];

			if (newValue !== undefined) {
				comparison.push({
					title: setting.title,
					currentValue: formatTemplateValue(setting.key, currentValue),
					newValue: formatTemplateValue(setting.key, newValue),
					key: setting.key,
					willChange: currentValue !== newValue,
				});
			}
		});

		return comparison;
	})();

	const handleConfirm = () => {
		dispatch('confirm');
		showModal = false;
	};

	const handleCancel = () => {
		dispatch('cancel');
		showModal = false;
	};
</script>

<Modal
	on:close={handleCancel}
	{showModal}
	title={$format('text_preview_modal_title', {
		default: 'Settings Preview',
	})}
>
	<Container slot="content">
		<Stack gap="m">
			<Alert
				type="info"
				heading={$format('text_preview_modal_heading', {
					default: 'Settings to be applied',
				})}
				message={$format('text_preview_modal_message', {
					default: 'The following settings will be applied to this device:',
				})}
			/>

			{#if settingsComparison.length > 0}
				<Text element="h3" class="mb-s">
					{$format('text_preview_device_heading', {
						default: 'Device: {deviceName}',
						values: { deviceName: device.name },
					})}
				</Text>

				{#each settingsComparison as setting}
					<Row class="mb-s">
						<Col>
							<BulletedList
								titleStyleType="body-small"
								descriptionStyleType="body-small"
								list={[
									{
										title: setting.title,
										description: setting.willChange
											? $format('text_preview_setting_change', {
													default: '{current} → {new}',
													values: {
														current: setting.currentValue,
														new: setting.newValue,
													},
												})
											: $format('text_preview_setting_same', {
													default: '{value} {noChangeSuffix}',
													values: {
														value: setting.newValue,
														noChangeSuffix: $format(
															'text_preview_no_change_suffix',
															{
																default: '(no change)',
															},
														),
													},
												}),
									},
								]}
							/>
						</Col>
					</Row>
				{/each}
			{:else}
				<Alert
					type="warning"
					heading={$format('text_preview_no_changes_heading', {
						default: 'No Changes Detected',
					})}
					message={$format('text_preview_no_changes_message', {
						default: 'The selected settings are already applied to this device.',
					})}
				/>
			{/if}
		</Stack>
	</Container>

	<div slot="footer" class="d-flex justify-content-end gap-s">
		<Button variant="secondary" on:click={handleCancel}>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button on:click={handleConfirm} disabled={settingsComparison.length === 0}>
			{$format('button_confirm_apply', { default: 'Confirm Apply' })}
		</Button>
	</div>
</Modal>
